import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";

// A work link (a plan page, a tile) can be opened by a browser session sitting in ANOTHER
// workspace — the key then resolves against the wrong workspace and the page can only say
// "not found" (owner, 2026-09-28: "when switching to browser it should switch workspaces").
// Server-side work URLs now carry `?ws=<teamId>`; this gate reads it, and when the session
// is elsewhere it switches the workspace (the platform's own cookie re-scope + reload,
// returning to this very URL) instead of rendering the wrong workspace's answer.

/** The workspaces the gate needs to know about — structural, not the full API type. */
export type GateWorkspace = { id: string; name: string; suspended?: boolean };

export type WorkspaceGate =
  | { kind: "ready" }
  | { kind: "pending" }
  | { kind: "switch"; target: GateWorkspace }
  | { kind: "blocked"; reason: "not-member" | "suspended"; ws: string };

/** Pure: what the `ws` hint resolves to against the session's own memberships. */
export function resolveWorkspaceGate(
  ws: string | undefined,
  current: { id: string } | undefined,
  workspaces: readonly GateWorkspace[] | undefined,
): WorkspaceGate {
  if (!ws) return { kind: "ready" };
  if (current && ws === current.id) return { kind: "ready" };
  if (!current || !workspaces) return { kind: "pending" };
  const target = workspaces.find((w) => w.id === ws);
  if (!target) return { kind: "blocked", reason: "not-member", ws };
  if (target.suspended) return { kind: "blocked", reason: "suspended", ws };
  return { kind: "switch", target };
}

/**
 * Reads the session's workspaces when a `ws` hint is present (or recovery was asked for),
 * auto-switches ONCE when the hint names another workspace the user belongs to, and offers
 * `switchTo` for the "plan/tile not found — try another workspace" recovery path.
 *
 * `hold` is true while the page must NOT run its own query (a switch is coming, or the
 * memberships are still loading). A memberships error fails OPEN to the old behaviour.
 */
export function useWorkspaceGate(ws: string | undefined, recover = false) {
  const api = useApi();
  const memberships = useQuery({
    queryKey: ["work", "workspace-gate"],
    queryFn: async () => ({
      current: (await api.workspaces.current()) as GateWorkspace,
      all: (await api.workspaces.list()) as GateWorkspace[],
    }),
    enabled: Boolean(ws) || recover,
    staleTime: 60_000,
    retry: false,
  });

  const gate = resolveWorkspaceGate(ws, memberships.data?.current, memberships.data?.all);

  const started = useRef(false);
  useEffect(() => {
    if (gate.kind === "switch" && !started.current) {
      started.current = true;
      // The adapter re-scopes the session cookie and reloads; returnTo keeps this deep link.
      void api.workspaces.switch(gate.target.id, {
        returnTo: `${window.location.pathname}${window.location.search}`,
      });
    }
  }, [gate, api]);

  const switchTo = (id: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("ws", id);
    void api.workspaces.switch(id, { returnTo: `${url.pathname}${url.search}` });
  };

  const current = memberships.data?.current;
  return {
    gate,
    hold: Boolean(ws) && !memberships.isError && gate.kind !== "ready",
    others: (memberships.data?.all ?? []).filter((w) => w.id !== current?.id && !w.suspended),
    switchTo,
    loaded: memberships.isSuccess,
  };
}

/** The screen shown while the gate holds the page: switching, resolving, or refusing by name. */
export function WorkspaceGateScreen({ gate }: { gate: WorkspaceGate }) {
  const { t } = useTranslation();
  if (gate.kind === "switch") {
    return (
      <div className="p-6 text-[color:var(--text-secondary)] text-sm">
        {t("This page lives in workspace “{{name}}” — entering it…", { name: gate.target.name })}
      </div>
    );
  }
  if (gate.kind === "blocked") {
    return (
      <div className="p-6">
        <p role="alert" className="text-[color:var(--danger)] text-sm">
          {gate.reason === "suspended"
            ? t("This page lives in a suspended workspace, which cannot be entered.")
            : t("This page lives in a workspace you are not a member of.")}
        </p>
      </div>
    );
  }
  return <div className="p-6 text-[color:var(--text-secondary)] text-sm">{t("Loading…")}</div>;
}

/** The recovery strip under a not-found error: the same page, tried in another workspace. */
export function WorkspaceRecovery({
  others,
  switchTo,
}: {
  others: GateWorkspace[];
  switchTo: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (others.length === 0) return null;
  return (
    <div className="mt-3 text-sm">
      <p className="text-[color:var(--text-secondary)]">
        {t("It may live in another of your workspaces:")}
      </p>
      <div className="mt-1 flex flex-wrap gap-2">
        {others.map((w) => (
          <button
            key={w.id}
            type="button"
            className="rounded-sm border border-[color:var(--border)] px-2 py-1 underline"
            onClick={() => switchTo(w.id)}
          >
            {t("Try in “{{name}}”", { name: w.name })}
          </button>
        ))}
      </div>
    </div>
  );
}
